import { faStepBackward, faStepForward } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { nanoid } from "@reduxjs/toolkit";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { useDispatch } from "react-redux";
import { useView } from "../../../hooks/useView";
import * as AnalyticsStore from "../../../store/analytics/Analytics";
import { calculateFramePeriod, getDisplayVideoAtTimestamp } from "../../../models/media/MediaSource";
import { Entity } from "../../../models/viz/Entity";
import useMediaSources from "../../../hooks/useMediaSources";
import { findClosest } from "../../../utilities/helpers";
import { useTheme } from "../../../hooks/useTheme";

// Used for skipping fragment groups with start times too close to the cursor position
const EVENT_BUFFER_MILLISECONDS = 200;

export function useVideoControls(
    viewId: string,
    mediaId: string,
    videoRef: MutableRefObject<HTMLVideoElement>,
    videoCanvasRef: MutableRefObject<HTMLCanvasElement>,
    sourceId: MutableRefObject<string>,
    entities: Entity[],
    entitiesInView: Entity[],
) {
    const dispatch = useDispatch();
    const { view } = useView(viewId);
    const { activeMediaSource: media } = useMediaSources(viewId, mediaId);

    const millisecondsBetweenFrames = calculateFramePeriod(media?.files?.initialDisplay);

    const areButtonControlsDisabled = !videoRef?.current || videoRef.current.src === "";

    const isPlaying = view?.mediaStates?.play === "playing";
    const isMuted = view?.mediaStates?.audio === "muted";

    const isPlayingEventsOnly = useRef(false);

    function mute() {
        dispatch(AnalyticsStore.actionCreators.setViewAudioState(viewId, "muted", sourceId.current));
    }

    function unmute() {
        dispatch(AnalyticsStore.actionCreators.setViewAudioState(viewId, "unmuted", sourceId.current));
        videoRef.current.muted = false;
    }

    function toggleMute() {
        isMuted ? unmute() : mute();
    }

    function pause() {
        dispatch(AnalyticsStore.actionCreators.setViewPlayState(viewId, "paused", sourceId.current));
        isPlayingEventsOnly.current = false;
        videoRef.current.autoplay = false;
        videoRef.current.pause();
        // Ensure cursor is at correct time after pausing video
        videoPlayerOnTimeUpdate();
    }

    function play() {
        dispatch(AnalyticsStore.actionCreators.setViewPlayState(viewId, "playing", sourceId.current));
        isPlayingEventsOnly.current = false;
        videoRef.current.play();
    }

    function togglePlay() {
        isPlaying ? pause() : play();
    }

    function updateCursor(updatedCursorMilliseconds: number) {
        if (view?.cursor === undefined || view?.cursor == updatedCursorMilliseconds) {
            return;
        }

        // Update video element's time too to avoid more updates from the VideoPlayer component
        if (videoRef.current) {
            videoRef.current.currentTime = (updatedCursorMilliseconds - media.startsAt) / 1000;
        }

        dispatch(
            AnalyticsStore.actionCreators.setPrimaryCursorPosition(
                viewId,
                updatedCursorMilliseconds,
                "controls",
                sourceId.current,
            ),
        );
    }

    function videoPlayerOnTimeUpdate() {
        if (!videoRef.current || !media || !view) {
            return;
        }

        const frameTime = videoRef.current.currentTime * 1000;
        const startTime = media.startsAt;
        const activeVideoStart = Number(videoRef.current.getAttribute("data-video-start")) || 0;
        const updatedCursorTime = startTime + frameTime + activeVideoStart;

        if (updatedCursorTime != view.cursor) {
            const attributeTime = Number(videoRef.current.getAttribute("data-timeline-time"));

            if (updatedCursorTime === attributeTime || videoRef.current.getAttribute("data-finished-load") !== "true") {
                // If the time is the same as the attribute, we can skip updating the cursor
                return;
            }

            dispatch(
                AnalyticsStore.actionCreators.setPrimaryCursorPosition(
                    viewId,
                    updatedCursorTime,
                    "playback",
                    sourceId.current,
                ),
            );
        }
    }

    function nextFrame() {
        const endTime = media.startsAt + media.duration;
        updateCursor(Math.min(view?.cursor + millisecondsBetweenFrames, endTime));
    }

    function prevFrame() {
        updateCursor(Math.max(view?.cursor - millisecondsBetweenFrames, media.startsAt));
    }

    function getEntityStartTimeMs(e: Entity): number {
        return e.getStartTimeOrElse(() => new Date()).getTime();
    }

    function prevEvent(bufferMilliseconds: number = EVENT_BUFFER_MILLISECONDS) {
        if (!entities?.length) {
            return;
        }
        const [closest, index] = findClosest(entities, view.cursor - bufferMilliseconds, getEntityStartTimeMs);

        if (getEntityStartTimeMs(closest) <= view.cursor - bufferMilliseconds) {
            updateCursor(getEntityStartTimeMs(closest));
        } else if (index !== 0) {
            updateCursor(getEntityStartTimeMs(entities[index - 1]));
        } else {
            pause();
        }
    }

    function nextEvent(bufferMilliseconds: number = EVENT_BUFFER_MILLISECONDS) {
        if (!entities?.length) {
            return;
        }
        const [closest, index] = findClosest(entities, view.cursor + bufferMilliseconds, getEntityStartTimeMs);

        if (getEntityStartTimeMs(closest) >= view.cursor + bufferMilliseconds) {
            updateCursor(getEntityStartTimeMs(closest));
        } else if (index !== entities.length - 1) {
            updateCursor(getEntityStartTimeMs(entities[index + 1]));
        } else {
            pause();
        }
    }

    function playEvents() {
        play();
        isPlayingEventsOnly.current = true;
    }

    function togglePlayEvents() {
        isPlaying ? pause() : playEvents();
    }

    useEffect(() => {
        if (isPlayingEventsOnly.current && entitiesInView?.length == 0) {
            nextEvent(0);
        }
    }, [entitiesInView?.length, isPlayingEventsOnly.current]);

    return {
        media,
        isPlaying,
        togglePlayEvents,
        isMuted,
        areButtonControlsDisabled,
        play,
        pause,
        mute,
        unmute,
        togglePlay,
        toggleMute,
        updateCursor,
        videoPlayerOnTimeUpdate,
        prevFrame,
        nextFrame,
        prevEvent,
        nextEvent,
    };
}

export interface VideoControlsProps {
    videoRef: MutableRefObject<HTMLVideoElement>;
    canvasRef: MutableRefObject<HTMLCanvasElement>;
    viewId: string;
    mediaId: string;
    entities: Entity[];
    entitiesInView: Entity[];
}

function VideoControls(props: VideoControlsProps) {
    const controlsId = useRef("cn-" + nanoid(8));
    const { isDarkTheme } = useTheme();
    const iconPrefix = "/images/icons/" + (isDarkTheme ? "dark-" : "");

    const {
        isPlaying,
        isMuted,
        areButtonControlsDisabled,
        togglePlay,
        toggleMute,
        prevFrame,
        nextFrame,
        prevEvent,
        nextEvent,
        togglePlayEvents,
    } = useVideoControls(
        props.viewId,
        props.mediaId,
        props.videoRef,
        props.canvasRef,
        controlsId,
        props.entities,
        props.entitiesInView,
    );

    const baseButtonProps = { disabled: areButtonControlsDisabled, variant: "secondary" };

    return (
        <div className="video-controls">
            <section>
                <Button
                    title="Previous event"
                    aria-label="previous event"
                    onClick={() => prevEvent()}
                    {...baseButtonProps}
                >
                    <img src={iconPrefix + "jump-to-previous-event_56.png"} />
                </Button>
                <Button
                    title={isPlaying ? "Pause" : "Play only events"}
                    aria-label="toggle play only events"
                    onClick={togglePlayEvents}
                    {...baseButtonProps}
                >
                    <img src={iconPrefix + (isPlaying ? "pause_56.png" : "play-events_56.png")} />
                </Button>
                <Button title="Next event" aria-label="next event" onClick={() => nextEvent()} {...baseButtonProps}>
                    <img src={iconPrefix + "jump-to-next-event_56.png"} />
                </Button>
            </section>

            <section>
                <div>
                    <Button title="Previous frame" aria-label="previous frame" onClick={prevFrame} {...baseButtonProps}>
                        <FontAwesomeIcon icon={faStepBackward} className="fa-fw" />
                    </Button>
                </div>
                <Button
                    title={isPlaying ? "Pause" : "Play"}
                    aria-label="toggle play"
                    onClick={togglePlay}
                    {...baseButtonProps}
                >
                    <img src={iconPrefix + (isPlaying ? "pause_56.png" : "play_56.png")} />
                </Button>
                <div>
                    <Button title="Next frame" aria-label="next frame" onClick={nextFrame} {...baseButtonProps}>
                        <FontAwesomeIcon icon={faStepForward} className="fa-fw" />
                    </Button>
                </div>
            </section>

            <Button title="Toggle audio" aria-label="toggle audio" onClick={toggleMute} {...baseButtonProps}>
                <img src={iconPrefix + (isMuted ? "disabled-audio_56.png" : "enabled-audio_56.png")} />
            </Button>
        </div>
    );
}

export default VideoControls;
